import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { Location, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, CanDeactivateFn, Router, RouterLink } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, Subject, Subscription, catchError, filter, finalize, firstValueFrom, forkJoin, map, of, take } from 'rxjs';
import Swal from 'sweetalert2';

import { QuotationService } from '@app/sales/quotation/quotation.service';
import { PreparedRentalContractInvoice, QuotationsService, RentalAnomaly, UnbillableRentalContract } from '@app/sales/quotations/quotations.service';
import { ProductsService } from '@app/sales/products/products.service';
import { SalesService } from '@app/sales/sales.service';
import { RegisterGateService } from '@app/sales/periods/register-gate.service';
import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { AlertService } from '@app/shared/alert.service';
import { showError } from '@app/shared/util';
import { ApiResponse, Attach, Partner } from '@app/shared/_models/common';
import { BillingTriggerEvent, Product } from '@app/shared/_models/product';
import { RentalSaleCancellation, Sales_order, SalesOrderForm } from '@app/shared/_models/sales';
import { C_VALUES_RENTAL_PERIOD, C_VALUES_UOM, DocType, EVENTTYP_INSERT, EVENTTYP_UPDATE, getDocTypeLabel, getOrderLabel, isOrderLineEditable, isSealedOrder, OrderLineType } from '@app/shared/constants';
import { EventType, SalesOrderState, cufSystem } from '@app/shared/constants-server';
import { RentalDeliveryDialogComponent } from '@app/rental/rental-delivery-dialog/rental-delivery-dialog.component';
import { RentalBlsComponent } from './rental-bls/rental-bls.component';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SearchPartnerComponent } from '@app/shared/search-partner/search-partner.component';
import { SmartSelectComponent } from '@app/shared/smart-select/smart-select.component';
import { PartnerService } from '@app/common/partner/partner.service';
import { openPartnerSheet } from '@app/common/partner/partner-sheet';
import { NewSaleService } from '@app/sales/quotation/new-sale.service';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { pickedDateStr, todayStr, yearsBefore } from '@app/shared/time';
import { RentalInvoicingDialogComponent, RentalInvoicingDialogResult, invoicingOutcome } from '@app/rental/rental-invoicing-dialog/rental-invoicing-dialog.component';
import { RentalLifecycleStore } from '@app/sales/rental-billing/rental-lifecycle.store';
import { RentalDecisionDialogsService } from '@app/sales/rental-billing/rental-decision-dialogs.service';
import { PatientLineAction, RentalPlanningComponent } from '@app/sales/rental-billing/planning/rental-planning.component';
import { RentalBillingService, serverMessage } from '@app/sales/rental-billing/rental-billing.service';
import { RentalPrescriptionRenewalService, blobErrorText } from '@app/rental/rental-prescription-renewal/rental-prescription-renewal.service';
import { isLineFile } from '@app/sales/rental-billing/rental-line-docs';
import { QuotationdocsComponent } from '@app/sales/quotation/quotationdocs/quotationdocs.component';
import { AttachService } from '@app/shared/attach/attach.service';
import { PrintService } from '@app/shared/print.service';
import { InvoicesService } from '@app/sales/invoices/invoices.service';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';
import { sameMemoLineIds } from '@app/sales/rental-billing/rental-planning.geometry';
import { docPatients, docTypeLabel, patientLabel, showInterruptedBadge, statusClass, statusLabel } from '@app/sales/rental-billing/rental-billing.labels';
import { frDay, money, qtyFr } from '@app/sales/rental-billing/rental-days';
import type { DecisionDialogResult, LineSheetDialogData } from '@app/sales/rental-billing/rental-dialogs.contract';
import type { ClosureSummary, RentalInvoiceView, RentalLifecycleFull, RentalLineView } from '@app/sales/rental-billing/rental-billing.model';

import { EMPTY_VIEW, HeaderProblem, headerProblems, InvoiceYear, invoiceYears, isOutOfContract, OutsideReturn, OtherLine, RentalGroup, RentalRow, RentalView, RowFilter, Level, buildRentalView, hiddenCount, levelCounts, parisDay, PresentPatient, presentPatients, visibleGroups, visibleLineIds } from './rental-contract.model';
import { periodAllowsSave } from './rental-period-gate';
import { freshLine, prepareRentalSave, rentalSaveBlockedReason } from './rental-contract.save';
import { RentalAddLineComponent } from './rental-add-line/rental-add-line.component';
import { VitrineRentalPickComponent } from './vitrine-rental-pick/vitrine-rental-pick.component';   // TEMPORAIRE (La Vitrine Médicale)
import { PatientDossier, RentalPatientService, mainDossier, newDossierBlockedReason, newDossierForm, partnerName, patientOf } from './rental-patient';
import { AddFormState, LinkedSale, ReturnFormState, addFormProblem, appendAddedLines, highestSeq, linkedProductsFor, linkedSaleOf, newOutOfContractReturnLine, newSaleLine, packagingNameOf, returnFormProblem } from './rental-contract.lines';
import { SaleOrderApi, SaleOrderHead, createSaleOrder, placeSaleLines, reusableSaleOrder, saleOrderHeadOf } from './rental-sale-order';

type Status = 'loading' | 'ok' | 'missing' | 'failed' | 'not-rental';
type Period = 'day' | 'month' | 'days30' | 'months12';
type Display = 'table' | 'planning';
type Gesture = (dossier: FormGroup) => string | null;   // geste rejoué sur la pièce relue : un refus à dire, null quand elle est prête à partir

// Aperçu du passage (prepare_invoicing) : `received` reste vrai pendant un recalcul, qui n'efface jamais la dernière réponse.
interface InvoicingState { loading: boolean; received: boolean; eligible: PreparedRentalContractInvoice | null; unbillable: UnbillableRentalContract | null; error: string | null; }
const NO_INVOICING: InvoicingState = { loading: false, received: false, eligible: null, unbillable: null, error: null };

// Demande de JP (28/10) : à l'arrivée, seul le matériel en cours de location ; les deux autres niveaux se chargent quand on les coche.
const DEFAULT_LEVELS: Record<Level, boolean> = { current: true, unbilled: false, closed: false };

// Camion d'un patient : pose à planifier ou BL ouvert (pose ou reprise −1) à revoir (plan), matériel en place (collect).
const PLAN_STATES: ReadonlySet<RentalRow['state']> = new Set(['to-plan', 'planned', 'return-planned']);
// Tout texte saisi (mémo, nom) mis dans un `html` de Swal passe par esc ; un titre saisi passe par `titleText` (audit 31/10 #9).
export const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

// Vers le dossier Téléchargements sans ouvrir d'onglet : octet-stream, comme le carrousel, pour qu'aucune visionneuse PDF ne l'intercepte.
const saveFile = (blob: Blob, name: string): void => {
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 200);
};

@Component({
  selector: 'app-rental-contract',
  templateUrl: './rental-contract.component.html',
  styleUrls: ['./rental-contract.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [RentalLifecycleStore],
  imports: [MenuCommonComponent, RentalAddLineComponent, VitrineRentalPickComponent, SearchPartnerComponent, SmartSelectComponent, ReactiveFormsModule, NgTemplateOutlet, SelectDateComponent, RentalPlanningComponent, RentalBlsComponent, ImageCarouselComponent, QuotationdocsComponent, RouterLink, FormsModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatTooltipModule, MatCheckboxModule, MatMenuModule, MatProgressSpinnerModule],
})
export class RentalContractComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private quotationService = inject(QuotationService);
  private quotationsService = inject(QuotationsService);
  private productsService = inject(ProductsService);
  private salesService = inject(SalesService);
  private valuesService = inject(ValuesService);
  private loginService = inject(LoginService);
  private registerGate = inject(RegisterGateService);
  private spin = inject(SpinnerService);
  private alertService = inject(AlertService);
  private dialog = inject(MatDialog);
  private dialogs = inject(RentalDecisionDialogsService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  public readonly store = inject(RentalLifecycleStore);
  private billing = inject(RentalBillingService);
  private attachService = inject(AttachService);
  private printService = inject(PrintService);
  private renewals = inject(RentalPrescriptionRenewalService);
  private invoicesService = inject(InvoicesService);
  private patients = inject(RentalPatientService);
  private partnerService = inject(PartnerService);
  private newSale = inject(NewSaleService);
  private location = inject(Location);
  rxFiles: Attach[] = []; // fichiers d'ordonnance du dossier, vignettes comme la fiche article
  private rxThumbs = new Map<number, string>();
  private rxFilesSub?: Subscription;
  readonly docsReload = new Subject<void>(); // chaque relecture de la pièce relit aussi ses documents (impression, signature de BL)
  readonly isLineFile = isLineFile;   // ordonnances et pièces d'un matériel : montrées sur le matériel (Planning), pas dans le dossier

  public readonly fr = frDay;
  public readonly m = money;
  public readonly q = qtyFr;
  public readonly statusLabel = statusLabel;
  public readonly statusClass = statusClass;
  public readonly docTypeLabel = docTypeLabel;
  public readonly docPatients = docPatients;

  public id = 0;
  public status: Status = 'loading';
  public loadError: string | null = null;
  public notRental: { docType: string; parentId: number | null } | null = null;
  public formGroup: FormGroup<SalesOrderForm> | null = null;
  public customer: any = null;
  public isHt = false;
  public view: RentalView = EMPTY_VIEW;
  public datstart: Date | undefined;
  public datend: Date | undefined;
  public levels: Record<Level, boolean> = { ...DEFAULT_LEVELS };
  public readonly levelBoxes: { key: Level; label: string; tip: string }[] = [
    { key: 'current', label: 'Matériel en cours de location', tip: 'Chez le patient, ou à livrer' },
    { key: 'unbilled', label: 'Matériel récupéré non facturé', tip: 'Repris, mais des jours restent à facturer ou une anomalie à lever ; lu à la demande' },
    { key: 'closed', label: 'Matériel clos', tip: 'Repris et entièrement facturé ; lu à la demande' },
  ];
  public counts: Record<Level, number> = { current: 0, unbilled: 0, closed: 0 };
  private levelsPending = false;
  public shownGroups: RentalGroup[] = [];
  public hidden = 0;
  public visibleIds: number[] = [];
  public lineActions: ReadonlyMap<number, PatientLineAction> = new Map();   // camion des patients du Planning
  private pendingSales: FormGroup[] = [];   // produits liés de « Livrer un matériel » : une commande de vente du dossier, créée après son enregistrement (JP 30/10)
  public salesToRetry: { head: SaleOrderHead; lines: FormGroup[] } | null = null;   // F2 en échec (#28) : lignes gardées, « Réessayer » sur la carte des ventes
  public saleOrders: Sales_order[] = [];    // commandes de vente du dossier (data.rental_order_id, FACTURE seulement), relues avec lui
  public saleOrdersError: string | null = null;
  public periodNames: Record<number, string> = {};
  private joined: RentalLifecycleFull | null = null;

  public display: Display = 'table';
  public planningSeen = false;
  public focusLineId: number | null = null;

  public invoicing: InvoicingState = NO_INVOICING;
  public showAnomalies = false;
  public problemsOpen = false;
  public problemsLeft = false;
  public problemsMax: number | null = null;
  private problemsOf: { view: RentalView; who: string | null; list: HeaderProblem[] } | null = null;
  public passing = false;
  public closureBusy = false;
  private invoicingSub?: Subscription;
  private loadSub?: Subscription;
  private destroyed = false;

  public adding = false;   // carte « Livrer un matériel » ouverte
  public editingIndex: number | null = null;
  public editingMemo = '';
  public saving = false;

  @ViewChild(RentalAddLineComponent) private addLine?: RentalAddLineComponent;
  @ViewChild('memoInput') set memoInputRef(el: ElementRef<HTMLInputElement> | undefined) {
    if (el) setTimeout(() => el.nativeElement.focus());
  }

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => { this.destroyed = true; this.rxThumbs.forEach((u) => URL.revokeObjectURL(u)); });
    this.store.attached$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadRxFiles());
    this.valuesService.getValues$(C_VALUES_RENTAL_PERIOD).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (periods: any[]) => {
        this.periodNames = Object.fromEntries((periods ?? []).map((p) => [p.id, p.name]));
        this.rebuild();
      },
      error: (e) => console.error('[RentalContract] périodes de location (c_values 16) illisibles', e),
    });
    // La vue serveur se joint aux rangées dès qu'elle arrive ; une décision enregistrée ne change que l'aperçu (la pièce reste la même).
    this.store.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.lifecycle !== this.joined) this.rebuild();
    });
    this.store.saved$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.refreshInvoicing());
    // F3 ou « Nouvelle Location » sur place : repartir d'un brouillon vierge, après confirmation si la saisie serait perdue.
    this.newSale.resetRequests$.pipe(filter((k) => k === 'rent'), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
      if (this.draft && (await this.canLeave())) this.initDraft();
    });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (this.route.snapshot?.data?.['draft']) { this.initDraft(); return; }
      const id = Number(params.get('id'));
      const next = Number.isInteger(id) && id > 0 ? id : 0;
      if (next !== this.id) this.resetFor(next);
      this.adding = false;
      this.pendingAdd = this.route.snapshot?.queryParamMap?.get('livrer') === '1';
      this.load();
    });
  }

  // Autre dossier : rien du précédent ne reste à l'écran (le store repart de zéro à son open).
  private resetFor(id: number): void {
    this.id = id;
    this.closedLoaded = false;
    this.levels = { ...DEFAULT_LEVELS };   // le store rouvert ne lit plus que les lignes en place
    // La vue suit l'adresse de chaque dossier ouvert (?vue=planning), sinon l'URL et l'écran divergent.
    this.display = this.route.snapshot?.queryParamMap?.get('vue') === 'planning' ? 'planning' : 'table';
    if (this.display === 'planning') this.planningSeen = true;
    this.loadSub?.unsubscribe();
    this.invoicingSub?.unsubscribe();
    this.formGroup = null;
    this.loadError = null;
    this.invoicing = NO_INVOICING;
    this.showAnomalies = false;
    this.problemsOpen = false;
    this.focusLineId = null;
    this.joined = null;
    this.view = EMPTY_VIEW;
    this.othersSub?.unsubscribe();
    this.othersFor = 0;
    this.others = [];
    this.mainId = null;
    this.patientId = null;
    this.loadedParties = null;
    this.picked.clear();
    this.pendingSales = [];
    this.salesToRetry = null;
    this.saleOrders = [];
    this.saleOrdersError = null;
    this.applyScope();
  }

  // --- Lecture de la pièce ---

  // Matériels repris et leurs reprises : ni lus ni calculés tant que l'on ne les demande pas (pièce o=open, vue ?open=true).
  public closedLoaded = false;

  loadClosed(): void {
    this.closedLoaded = true;
    this.store.loadClosed();
    this.load();
  }

  // `refreshCycle` : après une écriture de la pièce, la vue serveur est relue aussi (même dossier).
  load(after?: () => void, refreshCycle = false): void {
    this.loadSub?.unsubscribe();
    this.editingIndex = null;
    if (!this.id) { this.status = 'missing'; this.formGroup = null; return; }
    // Réessayer depuis l'échec garde le Planning affiché dessous : seul le spinner de blocage dit la lecture.
    if (!this.formGroup && this.status !== 'failed') this.status = 'loading';
    const id = this.id;
    this.loadSub = this.quotationService.getQuotationAsFormGroup(id, this.closedLoaded ? '?o=deliveries' : '?o=deliveries,open')
      .pipe(this.spin.block('Chargement du contrat…'), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fg) => {
          if (!fg) { this.status = 'missing'; this.formGroup = null; return; }
          const docType = fg.get('doc_type')?.value;
          const parentId = Number(fg.get('data')?.value?.parent_order_id) || null;
          if (docType !== DocType.LOCATION || parentId) {
            this.notRental = { docType: getDocTypeLabel(docType), parentId };
            this.status = 'not-rental';
            this.formGroup = null;
            return;
          }
          this.notRental = null;
          this.loadError = null;
          this.formGroup = fg;
          this.customer = fg.get('lnk_customer')?.value ?? null;
          this.isHt = !!this.customer?.data?.cufs?.[cufSystem.PARTNER_IS_HT];
          this.status = 'ok';
          this.rebuild();
          if (this.pendingAdd) this.openAddFromCreation();
          setTimeout(() => this.afterLoad(id, refreshCycle));   // après le premier rendu : la page n'attend jamais l'état de facturation
          after?.();
        },
        error: (err) => {
          // Relecture d'une pièce déjà affichée (après une écriture) : elle reste à l'écran, l'échec est signalé.
          if (this.formGroup && err?.status !== 404 && Number(this.formGroup.get('id')?.value) === id) {
            console.error('[RentalContract] relecture du contrat', err);
            this.alertService.error('Relecture du contrat en échec : ' + serverMessage(err));
            setTimeout(() => this.afterLoad(id, refreshCycle));
            return;
          }
          this.formGroup = null;
          this.status = err?.status === 404 ? 'missing' : 'failed';
          this.loadError = serverMessage(err);
          console.error('[RentalContract] lecture du contrat', err);
          // Un cycle au format ancien fait échouer la pièce entière ; /lifecycle le tolère : le Planning reste lisible.
          if (this.status === 'failed') setTimeout(() => this.afterLoad(id, refreshCycle));
        },
      });
  }

  private afterLoad(id: number, refreshCycle: boolean): void {
    if (this.destroyed || id !== this.id) return;   // page quittée ou autre dossier avant le tour suivant
    const same = this.store.orderId === id;
    this.store.open(id);
    if (refreshCycle && same) this.store.reload();
    this.refreshInvoicing();
    this.loadRxFiles();
    this.loadSaleOrders();
    this.docsReload.next();
    this.loadOthers(id);
    this.syncBeneficiary();
  }

  // --- Dossier unique par patient : mécanisme d'office, pas une règle (JP 29/10/2026) ---

  // Arrivée depuis /rental/nouveau (?livrer=1) : « Livrer un matériel » ouvert une fois, le paramètre retiré de l'adresse.
  private pendingAdd = false;

  private openAddFromCreation(): void {
    this.pendingAdd = false;
    this.router.navigate([], { relativeTo: this.route, queryParams: { livrer: null }, queryParamsHandling: 'merge', replaceUrl: true });
    if (this.editable) setTimeout(() => this.openAddForm());
  }

  // Bénéficiaire livré et payeur facturé, relus en entier (adresses, téléphone) quand le dossier a un bénéficiaire distinct.
  private loadedParties: { beneficiary: Partner | null; payer: Partner | null } | null = null;

  get distinctPayer(): boolean {
    if (this.draft) return this.payerDistinct && !!this.draftPayer;
    const r = Number(this.formGroup?.get('data')?.value?.recipient) || 0;
    return r > 0 && r !== Number(this.formGroup?.get('customer')?.value);
  }

  get partyBeneficiary(): Partner | null {
    return this.draft ? this.draftPatient : (this.loadedParties?.beneficiary ?? null);
  }

  get partyPayer(): Partner | null {
    return this.draft ? this.draftPayer : (this.loadedParties?.payer ?? this.customer ?? null);
  }

  get delivText(): string {
    return this.addressText(this.formGroup?.get('addr_deliv')?.value, Partner.addresses(this.partyBeneficiary));
  }

  get billText(): string {
    return this.addressText(this.formGroup?.get('addr_bill')?.value, Partner.addresses(this.partyPayer));
  }

  contactOf(p: Partner | null): string {
    return [p?.tel1, p?.email].filter((x) => !!x && String(x).trim()).join(' · ');
  }

  partyLabel(p: Partner | null): string {
    return partnerName(p);
  }

  // Nom du bénéficiaire ou du payeur : sa fiche tiers en fenêtre ; au retour, nom relu, et cartes relues (adresses, téléphone) hors brouillon.
  openParty(p: Partner | null | undefined): void {
    const id = Number(p?.id) || 0;
    if (!id) return;
    openPartnerSheet(this.dialog, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((back) => {
      if (!back?.id) return;
      const renamed = <T extends Partner | null>(x: T): T => (x && +x.id === +back.id ? { ...x, name: back.name, firstname: back.firstname } : x);
      this.customer = renamed(this.customer);
      if (this.draft) { this.draftPatient = renamed(this.draftPatient); this.draftPayer = renamed(this.draftPayer); }
      else if (this.distinctPayer) this.loadParties(this.id);
      else this.syncBeneficiary();
    });
  }

  private loadParties(id: number): void {
    this.loadedParties = null;
    if (!this.distinctPayer) return;
    const recipient = Number(this.formGroup?.get('data')?.value?.recipient);
    const customer = Number(this.formGroup?.get('customer')?.value);
    const one = (pid: number) => this.partnerService.getPartnerByid(pid).pipe(map((r: any) => (Array.isArray(r) ? r[0] : r) ?? null), catchError(() => of(null)));
    forkJoin({ beneficiary: one(recipient), payer: one(customer) }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      if (id !== this.id) return;
      this.loadedParties = p;
      this.syncBeneficiary();
    });
  }

  // Patient des lignes sans mémo (JP 30/10) : le bénéficiaire livré, donné au store pour le Tableau, le Planning et les fenêtres de décision.
  private syncBeneficiary(): void {
    if (this.draft || !this.formGroup || Number(this.formGroup.get('id')?.value) !== this.id) return;
    const p = this.distinctPayer ? this.loadedParties?.beneficiary : this.customer;
    if (p) this.store.setBeneficiary(this.id, partnerName(p));
  }

  // Ce que la page écrit à la place d'un mémo vide.
  patientText(memo: string | null | undefined): string {
    return patientLabel(memo, this.store.beneficiary);
  }

  public others: PatientDossier[] = [];
  public mainId: number | null = null;
  public patientId: number | null = null;
  private othersFor = 0;
  private othersSub?: Subscription;

  // Lus une fois par dossier, après l'affichage ; « principal » = le plus ancien du même payeur qui accepte des matériels, montré seulement si ce n'est pas celui-ci.
  private loadOthers(id: number): void {
    if (this.othersFor === id || !this.formGroup) return;
    this.othersFor = id;
    this.loadParties(id);
    const o = this.formGroup.getRawValue();
    const patient = patientOf(o);
    this.patientId = patient;
    if (!patient) return;
    const payer = Number(o.customer) !== patient ? Number(o.customer) : null;
    this.othersSub?.unsubscribe();
    const names = this.customer?.id ? { [Number(this.customer.id)]: partnerName(this.customer) } : {};   // payeur des autres dossiers dans l'infobulle
    this.othersSub = this.patients.dossiersOf$(patient, payer, names).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        if (id !== this.id) return;
        const main = mainDossier(list, Number(o.customer));
        this.mainId = main && main.id !== id ? main.id : null;
        this.others = list.filter((d) => d.id !== id && d.id !== this.mainId);
      },
      error: (e) => console.error('[RentalContract] autres dossiers du patient illisibles', e),
    });
  }

  // --- Brouillon (/rental/nouveau) : rien n'est écrit avant le premier matériel ; en-tête modifiable jusque-là (JP 29/10/2026) ---

  public draft = false;
  public draftPatient: Partner | null = null;
  public draftPayer: Partner | null = null;
  public payerDistinct = false;
  public another = false;   // ?autre=1 : second dossier voulu, l'existant ne se rouvre pas
  public draftBusy = false;
  public headerValidated = false;   // en-tête validé : figé, on passe aux lignes (JP 29/10/2026)
  public readonly resetDraftPatient = new Subject<boolean>();
  public readonly resetDraftPayer = new Subject<boolean>();
  private leaving = false;   // dossier enregistré : quitter le brouillon ne demande rien

  private initDraft(): void {
    this.resetFor(0);
    this.draft = true;
    this.leaving = false;
    this.headerValidated = false;
    this.adding = false;
    this.draftPatient = null;
    this.draftPayer = null;
    this.payerDistinct = false;
    this.customer = null;
    this.isHt = false;
    this.resetDraftPatient.next(true);
    this.resetDraftPayer.next(true);
    this.another = this.route.snapshot?.queryParamMap?.get('autre') === '1';
    this.status = 'ok';
    const customerId = Number(this.route.snapshot?.queryParamMap?.get('customer'));
    if (Number.isInteger(customerId) && customerId > 0) this.presetDraftPatient(customerId);
  }

  // Bouton « pour ce client » ou « + Autre dossier » (?customer=id) : le patient arrive choisi, comme pris dans la recherche.
  private presetDraftPatient(id: number): void {
    this.partnerService.getPartnerByid(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (partners: any) => {
        const p = Array.isArray(partners) ? partners[0] : partners;
        if (!p?.id) { this.alertService.error(`Client n° ${id} introuvable : choisissez-le dans la recherche`); return; }
        if (this.draft && !this.draftPatient) this.draftPatientChange(p);
      },
      error: (e) => this.alertService.error(`Client n° ${id} illisible : ${serverMessage(e)}`),
    });
  }

  get draftPatientName(): string {
    return partnerName(this.draftPatient);
  }

  get waitingPayer(): boolean {
    return !!this.draftPatient && this.payerDistinct && !this.draftPayer;
  }

  // Ce qui manque encore, dit sous l'en-tête du brouillon.
  get draftHint(): string | null {
    if (!this.draftPatient) return 'Choisissez le patient ou l\'établissement, puis « Valider l\'en-tête » : son dossier s\'ouvre s\'il en a déjà un, sinon on passe aux matériels (enregistré au premier livré).';
    if (this.waitingPayer) return `Choisissez le tiers payeur de ${this.draftPatientName} : le dossier lui sera facturé, livré chez le patient.`;
    if (!Number(this.formGroup?.get('addr_bill')?.value)) return "Le payeur n'a aucune adresse de facturation : ouvrez sa fiche (son nom, en haut) pour en ajouter une";
    return this.formGroup ? newDossierBlockedReason(this.formGroup, this.payerDistinct) : null;
  }

  // Adresses proposées : facturation chez le client (payeur sinon patient), livraison chez le patient, comme la F3 avec bénéficiaire.
  get billAddresses(): Partner[] {
    return Partner.addresses(this.customer);
  }

  get delivAddresses(): Partner[] {
    return Partner.addresses(this.draftPatient);
  }

  formatAddress = (ad: Partner): string => `${ad.street1 || ''} ${ad.zip || ''} ${ad.city || ''}`.replace(/\s+/g, ' ').trim();

  addressText(id: number | null | undefined, list: Partner[]): string {
    const a = list.find((x) => +x.id === Number(id));
    return a ? this.formatAddress(a) : '—';
  }

  // Seul geste qui quitte le choix du patient (JP 29/10) : le dossier existant du patient s'ouvre, sinon l'en-tête se fige et l'on passe aux lignes.
  async validateHeader(): Promise<void> {
    const reason = this.draftHint;
    if (reason || !this.formGroup) { this.alertService.warn(reason ?? 'En-tête incomplet'); return; }
    if (this.draftBusy) return;
    const patient = this.draftPatient!;
    const payer = Number(this.formGroup.get('customer')?.value);   // le patient lui-même, ou le tiers payeur coché
    if (!this.another) {
      this.draftBusy = true;
      let list: PatientDossier[] = [];
      try {
        list = await firstValueFrom(this.patients.dossiersOf$(Number(patient.id), this.payerDistinct ? this.draftPayer?.id : null).pipe(this.spin.block('Recherche du dossier du patient…')));
      } catch (e) {
        showError(`Les dossiers de ce patient sont illisibles.\nDétails: ${serverMessage(e)}`, 'ERREUR Serveur');
        return;
      } finally {
        this.draftBusy = false;
      }
      if (patient !== this.draftPatient || !this.draft) return;   // en-tête changé pendant la recherche
      const mine = list.filter((d) => d.customer === payer);   // un dossier payé par un autre ne s'ouvre jamais à la place (JP 04/11)
      const main = mainDossier(mine, payer);
      if (main) { this.openExisting(main.id, `${this.draftPatientName} a déjà le dossier n° ${main.id}`); return; }
      if (mine.length) {
        const last = mine[mine.length - 1];
        const r = await Swal.fire({ title: 'Aucun dossier ouvert à de nouveaux matériels', html: `Le dossier n° ${last.id} de ${esc(this.draftPatientName)} est « ${esc(getOrderLabel(last.state))} » et n'accepte plus de matériel.`, icon: 'question', showDenyButton: true, showCancelButton: true, confirmButtonText: 'Saisir un nouveau dossier', denyButtonText: `Ouvrir le n° ${last.id}`, cancelButtonText: 'Rester sur l\'en-tête' });
        if (r.isDenied) { this.openExisting(last.id, null); return; }
        if (!r.isConfirmed) return;
      }
    }
    this.headerValidated = true;
    this.openAddForm();
  }

  draftPatientChange(p: Partner | null | undefined): void {
    this.draftPatient = p?.id ? p : null;
    this.draftHeaderChanged();
  }

  draftPayerChange(p: Partner | null | undefined): void {
    this.draftPayer = p?.id ? p : null;
    this.draftHeaderChanged();
  }

  // Retour de la fiche ouverte par le nom : le tiers relu (adresse ajoutée…) remplace l'ancien, sans relancer la recherche.
  draftPatientRefreshed(p: Partner | null | undefined): void {
    if (p?.id && +p.id === +(this.draftPatient?.id ?? 0)) { this.draftPatient = p; this.draftHeaderChanged(); }
  }

  draftPayerRefreshed(p: Partner | null | undefined): void {
    if (p?.id && +p.id === +(this.draftPayer?.id ?? 0)) { this.draftPayer = p; this.draftHeaderChanged(); }
  }

  payerToggle(): void {
    if (!this.payerDistinct) { this.draftPayer = null; this.resetDraftPayer.next(true); }
    this.draftHeaderChanged();
  }

  // En-tête du brouillon : client = payeur sinon patient ; aucune recherche ici, le dossier existant ne s'ouvre qu'à « Valider l'en-tête ».
  private draftHeaderChanged(): void {
    const patient = this.draftPatient;
    const ready = !!patient && !this.waitingPayer;
    const payer = this.payerDistinct ? this.draftPayer : null;
    const before = { bill: Number(this.formGroup?.get('addr_bill')?.value) || 0, deliv: Number(this.formGroup?.get('addr_deliv')?.value) || 0 };
    this.customer = ready ? (payer ?? patient) : null;
    this.isHt = !!this.customer?.data?.cufs?.[cufSystem.PARTNER_IS_HT];
    this.formGroup = ready ? newDossierForm({ patient: patient!, payer, station: this.loginService.currentstation?.id ?? null, user: this.loginService.loggeduser?.id ?? null, store: this.loginService.currentstore ?? 0 }) : null;
    if (this.formGroup && this.billAddresses.some((a) => +a.id === before.bill)) this.formGroup.patchValue({ addr_bill: before.bill });
    if (this.formGroup && this.delivAddresses.some((a) => +a.id === before.deliv)) this.formGroup.patchValue({ addr_deliv: before.deliv });
    this.rebuild();
    if (!ready) this.adding = false;
  }

  // Appelé seulement depuis « Valider l'en-tête » : aucun matériel n'est encore en saisie, rien n'est perdu.
  private openExisting(id: number, why: string | null): void {
    this.leaving = true;
    if (why) this.alertService.success(`Dossier n° ${id} de ${this.draftPatientName} ouvert`);
    this.router.navigate(['/rental', id], { queryParams: { vue: 'planning', livrer: 1 }, replaceUrl: true });
  }

  // Premier matériel : une seule écriture (en-tête + lignes, comme la F3), puis la page du dossier ; la livraison se planifie à la main (JP 29/10).
  private async commitDraft(): Promise<void> {
    const fg = this.formGroup!;
    const abandon = () => { this.lines?.clear(); this.pendingSales = []; this.rebuild(); this.saving = false; };
    // Audit 31/10 #32 : sans cette seconde recherche, l'INSERT ne part pas (deux postes, même patient, même seconde) ; « Réessayer » la refait.
    while (!this.another) {
      try {
        const main = mainDossier(await firstValueFrom(this.patients.dossiersOf$(Number(this.draftPatient!.id), this.payerDistinct ? this.draftPayer?.id : null).pipe(this.spin.block('Recherche du dossier du patient…'))), Number(fg.get('customer')?.value));
        if (main) {   // créé sur un autre poste pendant la saisie
          abandon();
          await Swal.fire({ title: `Dossier n° ${main.id} déjà créé`, text: `Le dossier de ${this.draftPatientName} vient d'être créé sur un autre poste : il s'ouvre, livrez-y le matériel.`, icon: 'info' });
          this.leaving = true;
          this.router.navigate(['/rental', main.id], { queryParams: { vue: 'planning', livrer: 1 }, replaceUrl: true });
          return;
        }
        break;
      } catch (e) {
        console.error('[RentalContract] seconde recherche du dossier du patient', e);
        const r = await Swal.fire({ titleText: 'Dossier non enregistré', text: `Impossible de vérifier que ${this.draftPatientName} n'a pas déjà un dossier : ${serverMessage(e)}`, icon: 'error', showCancelButton: true, confirmButtonText: 'Réessayer', cancelButtonText: 'Abandonner ce matériel' });
        if (!r.isConfirmed) { abandon(); return; }
      }
    }
    // « + Autre dossier » : le serveur refuse un second dossier ouvert du patient sans ce drapeau (patient_dossier_refusal) ; seul cet INSERT le porte.
    const loose = fg as unknown as FormGroup;   // champ d'évènement hors du modèle SalesOrderForm
    if (this.another && !loose.contains('second_dossier')) loose.addControl('second_dossier', new FormControl(true));
    this.quotationService.postQuotationEvent(EVENTTYP_INSERT, fg)
      .pipe(this.spin.block('Enregistrement du dossier…'), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (response: ApiResponse) => {
          const newId = Number(response?.data?.order_id);
          if (!response?.success || !newId) {
            abandon();
            showError(`Le dossier n'a pas été enregistré.\nDétails: ${response?.msg || response?.error_msg || 'Erreur inconnue'}`, 'ERREUR Serveur');
            return;
          }
          this.leaving = true;
          this.alertService.success(`Dossier n° ${newId} enregistré pour ${this.draftPatientName}`);
          if (this.pendingSales.length) await this.createPendingSales(saleOrderHeadOf(fg.getRawValue(), newId), false);
          this.saving = false;   // #54 : tenu jusqu'à la fin de la commande de vente
          // Une reprise hors contrat seule n'a pas de rangée au Planning : le dossier s'ouvre sur le Tableau, où elle est listée.
          const ls = this.lines?.getRawValue() ?? [];
          const onlyOutside = ls.some(isOutOfContract) && !ls.some((l: any) => l.typ === OrderLineType.RENTAL && !isOutOfContract(l));
          this.router.navigate(['/rental', newId], { queryParams: onlyOutside ? {} : { vue: 'planning' }, replaceUrl: true });
        },
        error: (e) => {
          abandon();
          showError(`Le dossier n'a pas été enregistré.\nDétails: ${serverMessage(e)}`, 'ERREUR Serveur');
        },
      });
  }

  get draftFilled(): boolean {
    return this.draft && !this.leaving && (!!this.draftPatient || !!this.addLine?.form.product || !!this.addLine?.ret.product);
  }

  // Même question que F1, F2 et F3 (SALES_CONFIRM_DISCARD_DRAFT) : quitter un brouillon rempli efface la saisie.
  async canLeave(): Promise<boolean> {
    if (!this.draftFilled) return true;
    const r = await Swal.fire({ title: 'Abandonner la saisie en cours ?', text: "Le dossier de location n'a pas été enregistré. Quitter cette page l'effacera définitivement.", icon: 'warning', showCancelButton: true, confirmButtonText: 'Oui, tout effacer', cancelButtonText: 'Annuler' });
    if (r.isConfirmed) this.leaving = true;
    return r.isConfirmed;
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(ev: BeforeUnloadEvent): void {
    if (this.draftFilled) { ev.preventDefault(); ev.returnValue = true; }
  }

  // « Annuler » : la page d'avant dans l'application, sinon la liste des locations (la question de l'abandon est posée par la garde de sortie).
  cancelDraft(): void {
    if (this.router.lastSuccessfulNavigation()?.previousNavigation) this.location.back();
    else this.router.navigate(['/rental/rentals']);
  }

  otherTip(d: PatientDossier): string {
    return `${getOrderLabel(d.state)} · payé par ${d.customerName || 'client ' + d.customer}${d.acceptsLines ? '' : " · n'accepte plus de matériel"}`;
  }

  get hasRxLines(): boolean {
    return this.store.rentalLines().some((l) => l.params?.prescription_required === true);
  }

  renewalPrinting = false;

  // Lettre de renouvellement du patient du dossier, tous ses dossiers confondus : même route que l'écran du lot ; le refus du serveur (rien à demander) s'affiche tel quel.
  printRenewal(): void {
    if (!this.patientId || this.renewalPrinting) return;
    this.renewalPrinting = true;
    this.renewals.pdf$([this.patientId]).pipe(this.spin.block("Demande de renouvellement d'ordonnance…"), finalize(() => (this.renewalPrinting = false))).subscribe({
      next: (blob) => this.printService.printPdf(blob),
      error: (e) => blobErrorText(e).then((t) => this.alertService.error(t || 'ERREUR lors de la génération de la demande de renouvellement')),
    });
  }

  // Pièces du dossier marquées ordonnance ; vignette lue une fois par pièce (même mécanique que quotationdocs).
  loadRxFiles(): void {
    const id = this.id;
    if (!id) return;
    this.rxFilesSub?.unsubscribe();
    this.rxFilesSub = this.billing.getPrescriptionFiles(id).pipe(catchError(() => of([] as Attach[])), takeUntilDestroyed(this.destroyRef)).subscribe((files) => {
      if (id !== this.id) return;
      for (const a of files) {
        const known = a.id ? this.rxThumbs.get(a.id) : undefined;
        if (known) { a.thumbnailLocalUrl = known; continue; }
        this.attachService.loadThumbnailImage$(a).pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef)).subscribe((blob: any) => {
          if (!blob || !a.id) return;
          const url = this.rxThumbs.get(a.id) ?? URL.createObjectURL(blob);
          this.rxThumbs.set(a.id, url);
          a.thumbnailLocalUrl = url;
        });
      }
      this.rxFiles = files;
    });
  }

  // Vue serveur de CE dossier ; null avant son arrivée (ou pendant le passage à un autre dossier).
  get lifecycle(): RentalLifecycleFull | null {
    return this.store.orderId === this.id ? this.store.view : null;
  }

  get cycleError(): string | null {
    return this.store.orderId === this.id && !this.store.view ? this.store.error : null;
  }

  private rebuild(): void {
    this.joined = this.lifecycle;
    this.view = this.formGroup ? buildRentalView(this.formGroup.getRawValue() as any, this.periodNames, this.joined) : EMPTY_VIEW;
    const acts = new Map(this.view.rows.flatMap((r): [number, PatientLineAction][] => PLAN_STATES.has(r.state) ? [[r.id, r.state.startsWith('return') ? 'return' : 'deliver']] : r.canCollect ? [[r.id, 'collect']] : []));
    if (acts.size !== this.lineActions.size || [...acts].some(([id, a]) => this.lineActions.get(id) !== a)) this.lineActions = acts;   // même table : le Planning ne se recalcule pas
    this.applyScope();
  }

  get filter(): RowFilter {
    return { levels: this.levels, start: pickedDateStr(this.datstart), end: pickedDateStr(this.datend) };
  }

  private applyScope(): void {
    const f = this.filter;
    this.shownGroups = visibleGroups(this.view, f);
    this.hidden = hiddenCount(this.view, f);
    this.counts = levelCounts(this.view, f);
    this.levelsPending = !this.cycleError && this.view.rows.some((r) => r.state === 'returned' && !r.server && !r.isNew);
    const ids = visibleLineIds(this.view, f);
    if (ids.join() !== this.visibleIds.join()) this.visibleIds = ids;   // même tableau tant que rien ne change : le Planning ne se recalcule pas
  }

  dateChange(date: Date | undefined, which: 'datstart' | 'datend'): void {
    this[which] = date ?? undefined;
    this.applyScope();
  }

  // Matériel repris (soldé ou non) : absent de la lecture « open », il est lu en entier au premier niveau coché qui le montre.
  setLevel(level: Level, on: boolean): void {
    this.levels = { ...this.levels, [level]: on };
    if (on && level !== 'current' && !this.closedLoaded) this.loadClosed();
    this.applyScope();
  }

  // Compte d'un niveau repris inconnu tant que ces lignes ne sont pas lues, ou que leur statut serveur n'est pas arrivé.
  levelCount(level: Level): string {
    return level === 'current' || (this.closedLoaded && !this.levelsPending) ? ` (${this.counts[level]})` : '';
  }

  showAll(): void {
    this.datstart = this.datend = undefined;
    this.levels = { current: true, unbilled: true, closed: true };
    if (!this.closedLoaded) this.loadClosed();
    this.applyScope();
  }

  // Mêmes raccourcis que la liste des commandes (quotations.component.ts), tous jusqu'à aujourd'hui inclus.
  public readonly periods: { key: Period; label: string; title: string }[] = [
    { key: 'day', label: 'Jour', title: "Aujourd'hui" },
    { key: 'month', label: 'Mois', title: "Mois en cours : du 1er à aujourd'hui" },
    { key: 'days30', label: '30 j', title: "30 derniers jours, aujourd'hui compris" },
    { key: 'months12', label: '12 m', title: "12 derniers mois : du lendemain de la même date l'an dernier à aujourd'hui" },
  ];

  // Jour de Paris (todayStr) remis en date locale : pickedDateStr relit ces dates sans conversion de fuseau.
  private periodRange(key: Period): [Date, Date] {
    const [y, m1, d] = todayStr().split('-').map(Number);
    const m = m1 - 1;
    const today = new Date(y, m, d);
    if (key === 'month') return [new Date(y, m, 1), today];
    if (key === 'days30') return [new Date(y, m, d - 29), today];
    if (key === 'months12') { const s = yearsBefore(today, 1); s.setDate(s.getDate() + 1); return [s, today]; }
    return [new Date(y, m, d), today];
  }

  isPeriod(key: Period): boolean {
    const [s, e] = this.periodRange(key);
    return pickedDateStr(this.datstart) === pickedDateStr(s) && pickedDateStr(this.datend) === pickedDateStr(e);
  }

  setPeriod(key: Period): void {
    [this.datstart, this.datend] = this.periodRange(key);
    this.applyScope();
  }

  get activePeriod(): Period | null {
    return this.periods.find((p) => this.isPeriod(p.key))?.key ?? null;
  }

  // --- Tableau | Planning : le Planning n'est créé qu'au premier passage, puis gardé (masqué) ---

  setDisplay(display: Display, focus: number | null = null): void {
    this.display = display;
    if (display === 'planning') this.planningSeen = true;
    this.focusLineId = null;
    if (focus !== null) setTimeout(() => (this.focusLineId = focus));   // une même ligne redemandée redevient une nouvelle valeur
    this.router.navigate([], { relativeTo: this.route, queryParams: { vue: display === 'planning' ? 'planning' : null }, queryParamsHandling: 'merge', replaceUrl: true })
      .catch((e) => console.error('[RentalContract] ?vue non reflétée dans l’adresse', e));
  }

  // --- Aperçu du passage : le dernier montant reste affiché pendant le recalcul ---

  refreshInvoicing(): void {
    if (this.status !== 'ok' || !this.id) return;
    this.invoicingSub?.unsubscribe();
    const id = this.id;
    this.invoicing = { ...this.invoicing, loading: true, error: null };
    this.invoicingSub = this.quotationsService.prepareRentalInvoicing([id]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        if (id !== this.id) return;
        const eligible = res?.eligible_invoices?.find((e) => e.order_id === id) ?? res?.eligible_invoices?.[0] ?? null;
        const unbillable = eligible ? null : res?.unbillable_orders?.find((u) => u.order_id === id) ?? res?.unbillable_orders?.[0] ?? null;
        this.invoicing = { loading: false, received: true, eligible, unbillable, error: null };
      },
      error: (e) => {
        if (id === this.id) this.invoicing = { ...this.invoicing, loading: false, error: serverMessage(e) };
      },
    });
  }

  // Montant principal dans l'unité du client (HT pour un client HT), l'autre en second ; avoirs prévus tels que rendus (≤ 0).
  get invoiceMain(): number | null {
    const e = this.invoicing.eligible;
    return e ? Number(this.isHt ? e.amount_taxx : e.amount_taxi) || 0 : null;
  }

  get invoiceOther(): number {
    const e = this.invoicing.eligible;
    return e ? Number(this.isHt ? e.amount_taxi : e.amount_taxx) || 0 : 0;
  }

  get invoiceCredit(): number {
    const e = this.invoicing.eligible;
    return e ? Number(this.isHt ? e.credit_taxx : e.credit_taxi) || 0 : 0;
  }

  get invoiceReason(): string | null {
    return this.invoicing.received && !this.invoicing.eligible ? this.invoicing.unbillable?.reason || 'Rien à facturer' : null;
  }

  get invoiceAnomalies(): RentalAnomaly[] {
    return this.invoicing.eligible?.anomalies ?? this.invoicing.unbillable?.anomalies ?? [];
  }

  get invoiceBlockingCount(): number {
    return this.invoiceAnomalies.filter((a) => a.blocked).length;
  }

  get invoiceAnomaliesLabel(): string {
    return `${this.plural(this.invoiceAnomalies.length, 'anomalie', 'anomalies')} (${this.plural(this.invoiceBlockingCount, 'bloquante', 'bloquantes')})`;
  }

  get invoiceBlockedReason(): string | null {
    if (this.passing) return 'Préparation en cours';
    if (!this.invoicing.received) return 'Aperçu en cours de calcul';
    if (!this.invoicing.eligible) return this.invoiceReason;
    return null;
  }

  anomalyWhere(a: RentalAnomaly): string {
    if (a.origin_order_l_id == null) return 'Dossier';
    const id = Number(a.origin_order_l_id);
    const r = this.view.rows.find((x) => x.id === id);
    if (r) return `ligne ${r.seq}, ${r.productName}${r.patient ? ' (' + r.patient + ')' : ''}`;
    const o = this.view.others.find((x) => x.id === id);
    return o ? `ligne ${o.seq}, ${o.name}` : `ligne ${id}`;
  }

  // Passage 2101 : même chaîne que la F4 (rental/rentals/rentals.ts, onInvoice), pour ce seul dossier.
  invoiceNow(): void {
    if (this.invoiceBlockedReason || !this.id) return;
    this.passing = true;
    const id = this.id;
    this.registerGate.check$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((gate) => {
      if (!gate.open) {
        this.passing = false;
        this.registerGate.redirectToPeriods(gate);
        return;
      }
      this.quotationsService.prepareRentalInvoicing([id])
        .pipe(this.spin.block('Préparation de la facturation des locations…'), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (resp) => {
            this.passing = false;
            this.dialog.open(RentalInvoicingDialogComponent, {
              width: '90%', height: '85%', maxWidth: '95vw', disableClose: true, autoFocus: false,
              data: { eligibleInvoices: resp?.eligible_invoices ?? [], unbillableOrders: resp?.unbillable_orders ?? [] },
            }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result?: RentalInvoicingDialogResult) => {
              const out = invoicingOutcome(result);   // « réussi » = une pièce créée (#33)
              if (out) this.alertService[out.kind](out.text);
              if (id === this.id) this.load(undefined, true);
            });
          },
          error: (e) => {
            this.passing = false;
            this.alertService.error('Préparation de la facturation impossible : ' + serverMessage(e));
          },
        });
    });
  }

  // --- Clôture du dossier (évènement 2107) : le serveur refuse avec le motif de chaque ligne qui bloque ---

  get closure(): ClosureSummary | null {
    return this.lifecycle?.closure ?? null;
  }

  async closeDossier(): Promise<void> {
    const r = await Swal.fire({ title: `Clôturer le dossier n° ${this.id} ?`, text: "Toutes ses lignes louées et vendues seront figées : plus de facture, d'avoir ni de décision tant qu'il n'est pas rouvert.", icon: 'question', showCancelButton: true, confirmButtonText: 'Clôturer', cancelButtonText: 'Annuler' });
    if (r.isConfirmed) this.sendClosure('close');
  }

  async reopenDossier(): Promise<void> {
    const r = await Swal.fire({ title: `Rouvrir le dossier n° ${this.id} ?`, text: 'Ses lignes closes redeviennent modifiables et facturables.', input: 'text', inputLabel: 'Motif', inputValidator: (v) => (v?.trim() ? null : 'Le motif est obligatoire'), icon: 'question', showCancelButton: true, confirmButtonText: 'Rouvrir', cancelButtonText: 'Annuler' });
    if (r.isConfirmed) this.sendClosure('reopen', String(r.value ?? ''));
  }

  private sendClosure(op: 'close' | 'reopen', reason?: string): void {
    const id = this.id;
    this.closureBusy = true;
    this.billing.postClosure(id, op, reason).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.closureBusy = false;
      if (res.ok) {
        this.alertService.success(res.message || (op === 'close' ? 'Dossier clos' : 'Dossier rouvert'));
        if (id === this.id) this.load(undefined, true);
        return;
      }
      const items = res.message.replace(/^clôture refusée — /, '').split(' · ').map((m) => `<li style="text-align:left;margin:4px 0">${esc(m)}</li>`).join('');
      Swal.fire({ title: op === 'close' ? 'Clôture refusée' : 'Réouverture refusée', html: `<ul style="padding-left:18px">${items}</ul>`, icon: 'warning' });
    });
  }

  // --- Décisions : une par ligne cochée, via les dialogues communs ; aucune garde « lecture seule » (le serveur les accepte en tout état) ---

  // Ligne sur laquelle une décision est possible : enregistrée, vue serveur arrivée, cycle lisible.
  decisionLine(r: RentalRow): RentalLineView | null {
    const l = r.server;
    return l && !l.lifecycle_unreadable && l.decisions != null ? l : null;
  }

  menuBlockedReason(r: RentalRow): string | null {
    if (r.isNew) return 'Ligne non enregistrée';
    if (r.server) return null;
    if (this.cycleError) return 'État de facturation indisponible';
    return this.lifecycle && !this.store.refreshing ? "Ligne absente de l'état de facturation" : 'État de facturation en cours de chargement';
  }

  decisionBlockedReason(r: RentalRow): string | null {
    const l = r.server;
    if (!l) return this.menuBlockedReason(r);
    return this.decisionLine(r) ? null : l.anomalies[0] || 'Cycle de location illisible : aucune décision possible';
  }

  startBlockedReason(r: RentalRow): string | null {
    const l = this.decisionLine(r);
    if (!l) return this.decisionBlockedReason(r);
    if (!l.realdatdel) return 'Ligne pas encore livrée';
    if (l.params?.start_before_delivery !== true) return "Le contrat de location n'autorise pas de facturer avant le BL";
    return null;
  }

  // Interruption, geste commercial compris (motif offert, plus d'entrée de menu à part depuis le 27/10/2026) :
  // la ligne et ses voisines de même mémo cochées (lit et matelas d'un résident).
  openRange(r: RentalRow): void {
    const l = this.decisionLine(r);
    if (!l) return;
    this.follow(this.dialogs.openRange({ store: this.store, orderId: this.id, origin: 'menu', lineIds: sameMemoLineIds(l, this.store.rentalLines()) }));
  }

  openWindow(r: RentalRow, bound: 'START' | 'END'): void {
    const l = this.decisionLine(r);
    if (!l || (bound === 'START' && this.startBlockedReason(r))) return;
    this.follow(this.dialogs.openWindow({ store: this.store, orderId: this.id, bound, lineIds: [l.id] }));
  }

  openPrescription(r: RentalRow): void {
    const l = this.decisionLine(r);
    if (l) this.follow(this.dialogs.openPrescription({ store: this.store, orderId: this.id, lineId: l.id }));
  }

  openSheet(r: RentalRow, section?: LineSheetDialogData['section']): void {
    if (r.server) this.follow(this.dialogs.openLineSheet({ store: this.store, orderId: this.id, lineId: r.server.id, section }));
  }

  get problems(): HeaderProblem[] {
    if (this.problemsOf?.view !== this.view || this.problemsOf.who !== this.store.beneficiary) this.problemsOf = { view: this.view, who: this.store.beneficiary, list: headerProblems(this.view, this.store.beneficiary) };
    return this.problemsOf.list;
  }

  // Le geste qui règle le point : ordonnance à saisir sur ce seul matériel, sinon sa fiche, sinon le tableau (récupération orpheline, vue serveur absente).
  problemAction(p: HeaderProblem): string {
    if (p.row && p.rx && this.decisionLine(p.row)) return "Ajouter l'ordonnance";
    return p.row?.server ? 'Ouvrir la fiche' : 'Voir dans le tableau';
  }

  onProblem(p: HeaderProblem): void {
    this.problemsOpen = false;
    const r = p.row;
    if (r && p.rx && this.decisionLine(r)) this.openPrescription(r);
    else if (r?.server) this.openSheet(r, 'anomalies');
    else this.setDisplay('table');
  }

  // Liste déployée vers la gauche sous la pastille ; si la place manque (en-tête replié, écran étroit), du côté le plus large, à sa largeur.
  openProblems(wrap: HTMLElement): void {
    const r = wrap.getBoundingClientRect();
    const toLeft = r.right - 16, toRight = window.innerWidth - r.left - 16;
    this.problemsLeft = toLeft < 560 && toRight > toLeft;
    this.problemsMax = toLeft >= 560 ? null : Math.max(240, Math.floor(Math.max(toLeft, toRight)));
    this.problemsOpen = true;
  }

  problemsFocusOut(e: FocusEvent): void {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node | null)) this.problemsOpen = false;
  }

  showInPlanning(r: RentalRow): void {
    this.setDisplay('planning', r.id > 0 ? r.id : null);
  }

  openSaleCancellation(o: OtherLine): void {
    if (o.sale) this.follow(this.dialogs.openSaleCancellation({ store: this.store, orderId: this.id, lineId: o.id }));
  }

  cancelSaleCancellation(o: OtherLine, c: RentalSaleCancellation): void {
    if (!o.sale || c.cancelled) return;
    this.follow(this.dialogs.openCancelDecision({
      store: this.store, orderId: this.id, typ: EventType.RENTAL_SALE_CANCELLATION, targets: [{ lineId: o.id, id: c.id, checked: true }],
      title: "Annuler l'annulation de vente",
      summary: `${o.name} : annulation de ${qtyFr(c.qty)} saisie le ${frDay(parisDay(c.created.dat))} (${this.store.reasonName(c.reason_id)})`,
    }));
  }

  // Le dialogue enregistre lui-même ; le store rechargé émet saved$, qui relance l'aperçu.
  private follow(result$: Observable<DecisionDialogResult>): void {
    result$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  // --- Lectures pour le gabarit ---

  get lines(): FormArray | null {
    return (this.formGroup?.get('lines') as FormArray) ?? null;
  }

  get editable(): boolean {
    return this.status === 'ok' && (!this.draft || this.headerValidated) && isOrderLineEditable(this.formGroup?.get('state')?.value, this.formGroup?.get('doc_type')?.value);
  }

  // Joindre un document reste permis sur un dossier en cours (lignes figées) ; seule une pièce scellée l'interdit, comme l'onglet de l'écran actuel.
  get docsEditable(): boolean {
    return this.status === 'ok' && !isSealedOrder(this.formGroup?.get('state')?.value);
  }

  get customerLabel(): string {
    const name = `${this.customer?.firstname ?? ''} ${this.customer?.name ?? ''}`.trim();
    return name || 'Client non renseigné';
  }

  get openedOn(): string {
    return frDay(parisDay(this.formGroup?.get('dates')?.value?.created));
  }

  get stateLabel(): string {
    return getOrderLabel(this.formGroup?.get('state')?.value);
  }

  get taxLabel(): string {
    return this.isHt ? 'HT' : 'TTC';
  }

  periodName(id: number | null): string {
    return (id != null ? this.periodNames[id] : '')?.toLowerCase() || 'période';
  }

  groupPrice(g: RentalGroup): string {
    return `${money(this.isHt ? g.unitTaxx : g.unitTaxi)} € ${this.taxLabel} par ${g.periodLabel}`;
  }

  rank(l: RentalLineView): string {
    return `${l.rank}/${l.duration != null && l.duration > 0 ? l.duration : '∞'}`;
  }

  interruptedBadge(l: RentalLineView): boolean {
    const asOf = this.lifecycle?.as_of;
    return !!asOf && showInterruptedBadge(l, asOf);
  }

  anomalyTip(r: RentalRow): string {
    return r.anomalies.map((a) => (a.server ? (a.blocked ? 'Bloquante : ' : '') + a.detail : `${a.label} : ${a.detail}`)).join('\n');
  }

  blockedRow(r: RentalRow): boolean {
    return r.anomalies.some((a) => a.blocked);
  }

  // Jours couverts par la pièce, start et end inclus.
  invoicePeriod(inv: RentalInvoiceView): string {
    if (!inv.start) return '—';
    return inv.end && inv.end !== inv.start ? `du ${frDay(inv.start)} au ${frDay(inv.end)}` : `le ${frDay(inv.start)}`;
  }

  invoiceState(inv: RentalInvoiceView): string {
    return getOrderLabel(inv.state);
  }

  // --- Factures et avoirs : PDF par le lien d'« Imprimer la facture » (rendu et compté au JET à chaque appel), jamais un fichier rangé ---
  public picked = new Set<number>();     // pièces cochées, téléchargées ensemble
  public fetching = new Set<number>();   // PDF en cours de rendu, par pièce
  public batch: { done: number; total: number } | null = null;
  private yearsFor: RentalInvoiceView[] | null = null;
  private years: InvoiceYear[] = [];

  invoiceGroups(list: RentalInvoiceView[]): InvoiceYear[] {
    if (list !== this.yearsFor) { this.yearsFor = list; this.years = invoiceYears(list); }
    return this.years;
  }

  // Pourquoi une pièce n'a pas de PDF, null si elle en a : la reprise d'orthop n'en produit pas, une pièce sans numéro n'est pas scellée.
  noPdf(inv: RentalInvoiceView): string | null {
    if (isSealedOrder(inv.state)) return "Pièce reprise d'orthop : pas de PDF";
    return inv.doc_ref ? null : 'Pièce sans numéro : pas de PDF';
  }

  hasPdf(list: RentalInvoiceView[]): boolean {
    return list.some((inv) => !this.noPdf(inv));
  }

  pickState(list: RentalInvoiceView[]): 'all' | 'some' | 'none' {
    const ids = list.filter((inv) => !this.noPdf(inv)).map((inv) => inv.id);
    const n = ids.filter((id) => this.picked.has(id)).length;
    return n === 0 ? 'none' : n === ids.length ? 'all' : 'some';
  }

  pickAll(list: RentalInvoiceView[], on: boolean): void {
    for (const inv of list) if (!this.noPdf(inv)) this.togglePick(inv, on);
  }

  togglePick(inv: RentalInvoiceView, on: boolean): void {
    if (on) this.picked.add(inv.id);
    else this.picked.delete(inv.id);
  }

  // Cochées et encore listées, dans l'ordre affiché (le plus récent d'abord).
  get pickedInvoices(): RentalInvoiceView[] {
    const lc = this.lifecycle;
    return lc ? this.invoiceGroups(lc.invoices).flatMap((g) => g.invoices).filter((inv) => this.picked.has(inv.id) && !this.noPdf(inv)) : [];
  }

  private invoicePdf$(inv: RentalInvoiceView): Observable<Blob> {
    return this.printService.getOrderJustificatif$(inv.id, 'PRINT_INVOICE_A4', inv.doc_ref ?? undefined);
  }

  openInvoicePdf(inv: RentalInvoiceView): void {
    if (!this.noPdf(inv)) this.invoicesService.openPdf(this.invoicePdf$(inv));
  }

  async downloadInvoice(inv: RentalInvoiceView): Promise<void> {
    if (this.noPdf(inv) || this.fetching.has(inv.id)) return;
    const failed = await this.fetchInvoice(inv);
    if (failed) showError(`La pièce ${inv.doc_ref} n'a pas pu être générée.\nDétails : ${failed}`, 'Facture non disponible');
  }

  // Une pièce après l'autre : le navigateur demande une fois d'autoriser les téléchargements multiples ; les échecs sont dits ensemble à la fin.
  async downloadPicked(): Promise<void> {
    const targets = this.pickedInvoices;
    if (!targets.length || this.batch) return;
    const failures: string[] = [];
    let done = 0;
    this.batch = { done, total: targets.length };
    for (const inv of targets) {
      const failed = await this.fetchInvoice(inv);
      if (failed) failures.push(`${inv.doc_ref} : ${failed}`);
      this.batch = { done: ++done, total: targets.length };
    }
    this.batch = null;
    if (failures.length) showError(`${failures.length} pièce(s) sur ${targets.length} non téléchargée(s).\n${failures.join('\n')}`, 'Téléchargement incomplet');
  }

  // Motif de l'échec, null quand le fichier est parti.
  private async fetchInvoice(inv: RentalInvoiceView): Promise<string | null> {
    this.fetching.add(inv.id);
    try {
      saveFile(await firstValueFrom(this.invoicePdf$(inv)), `${inv.doc_ref}.pdf`);
      return null;
    } catch (e: any) {
      const serverMsg = e?.error instanceof Blob ? await e.error.text().catch(() => '') : e?.error?.message || e?.error;
      return serverMsg || e?.message || 'Erreur inconnue';
    } finally {
      this.fetching.delete(inv.id);
    }
  }

  cancellationText(c: RentalSaleCancellation): string {
    const comment = c.comment?.trim() ? ` · ${c.comment.trim()}` : '';
    return `Annulation de ${qtyFr(c.qty)} · ${this.store.reasonName(c.reason_id)}${comment} · saisie par ${this.store.userName(c.created.id_user)} le ${frDay(parisDay(c.created.dat))}`;
  }

  cancelledText(c: RentalSaleCancellation): string {
    const x = c.cancelled!;
    return `Annulée le ${frDay(parisDay(x.dat))} par ${this.store.userName(x.id_user)} : ${x.reason}`;
  }

  plural(n: number, one: string, many: string): string {
    return `${n} ${n > 1 ? many : one}`;
  }

  get hiddenLabel(): string {
    return this.plural(this.hidden, 'matériel masqué par les filtres', 'matériels masqués par les filtres');
  }

  get deliveryBlockedReason(): string | null {
    return this.formGroup?.get('addr_deliv')?.value ? null : 'Aucune adresse de livraison sur ce document : choisissez-en une avant de planifier';
  }

  get saveBlockedReason(): string | null {
    return rentalSaveBlockedReason(this.formGroup, this.customer);
  }

  trackRow = (_: number, r: RentalRow) => `${r.index}:${r.id}`;
  trackOutside = (_: number, o: OutsideReturn) => `${o.index}:${o.id}`;

  // --- Enregistrement : la pièce relue repart entière avec le seul geste, le serveur supprime ou refuse ce qui manque ---

  // Gardes AVANT toute modification du formulaire : un refus laisse la pièce telle que lue.
  private async guardSave(): Promise<boolean> {
    if (this.saving || this.spin.isLoading) { this.alertService.warn('Un enregistrement est déjà en cours'); return false; }
    if (!this.editable || !this.formGroup) return false;
    const blocked = this.saveBlockedReason;
    if (blocked) { this.alertService.warn(blocked); return false; }
    this.saving = true;
    if (await periodAllowsSave(this.salesService, this.spin, 'Enregistrement du contrat…')) return true;
    this.saving = false;
    return false;
  }

  // Audit 31/10 #8 : la pièce lue à l'ouverture ne repart jamais ; relue juste avant l'UPDATE, seul le geste y est rejoué (comme la vue du payeur).
  // `collect` : matériels posés à reprendre, mis à −1 dans la fenêtre de livraison ouverte après l'enregistrement.
  private async commit(gesture: Gesture, createdLine = false, patient?: string, collect: number[] = []): Promise<void> {
    if (this.draft) {
      const refused = gesture(this.formGroup!);
      if (refused) { this.saving = false; this.alertService.warn(refused); return; }
      this.rebuild();
      await this.commitDraft();
      return;
    }
    const id = this.id;
    const fg = await this.readDossier();
    if (!fg || id !== this.id) { this.saving = false; return; }
    const customer: Partner | null = fg.get('lnk_customer')?.value ?? null;
    const blocked = rentalSaveBlockedReason(fg, customer);
    if (blocked) { this.saving = false; this.alertService.warn(blocked); return; }
    const known = new Set(((fg.get('lines') as FormArray).getRawValue() ?? []).map((l: any) => Number(l.id) || 0));	// les lignes neuves (matériel, reprise) sont celles que la relecture ajoute
    const refused = gesture(fg);
    if (refused) {
      this.saving = false;
      this.pendingSales = [];
      await Swal.fire({ titleText: 'Dossier modifié entre-temps', text: refused, icon: 'warning' });
      if (id === this.id) this.load(undefined, true);
      return;
    }
    prepareRentalSave(fg, customer?.id, this.loginService.currentstore ?? 0);
    let response: ApiResponse;
    try {
      response = await firstValueFrom(this.quotationService.postQuotationEvent(EVENTTYP_UPDATE, fg).pipe(this.spin.block('Enregistrement du contrat…')));
    } catch (e) {
      this.saving = false;
      this.pendingSales = [];
      showError(`Le contrat n'a pas été enregistré.\nDétails: ${serverMessage(e)}`, 'ERREUR Serveur');
      this.load();
      return;
    }
    if (!response?.success) {
      this.saving = false;
      this.pendingSales = [];
      showError(`Le contrat n'a pas été enregistré.\nDétails: ${response?.msg || response?.error_msg || 'Erreur inconnue'}`, 'ERREUR Serveur');
      this.load();
      return;
    }
    const newId = Number(response.data?.order_id) || id;
    this.alertService.success('Enregistrement OK : ' + newId);
    if (newId !== id) { this.saving = false; this.pendingSales = []; this.router.navigate(['/rental', newId]); return; }
    // #54 : `saving` tenu jusqu'à la fin de la commande de vente, aucun second geste entre-temps.
    const saleIds = this.pendingSales.length ? await this.createPendingSales(saleOrderHeadOf(fg.getRawValue(), newId)) : [];
    this.saving = false;
    if (id !== this.id) return;
    this.load(createdLine ? () => this.openDeliveryDialog((this.lines?.getRawValue() ?? []).map((l: any) => Number(l.id) || 0).filter((x: number) => x && !known.has(x)), patient, collect, saleIds) : undefined, true);
  }

  // Pièce relue comme à l'ouverture (sans les lignes closes tant qu'elles ne sont pas lues : le serveur les garde) ; refus dits à l'écran.
  private async readDossier(): Promise<FormGroup | null> {
    const id = this.id;
    let fg: FormGroup | undefined;
    try {
      fg = await firstValueFrom(this.quotationService.getQuotationAsFormGroup(id, this.closedLoaded ? '?o=deliveries' : '?o=deliveries,open').pipe(this.spin.block('Relecture du dossier…')));
    } catch (e) {
      this.alertService.error("Relecture du dossier en échec, rien n'est enregistré : " + serverMessage(e));
      return null;
    }
    if (!fg) { this.alertService.warn(`Dossier n° ${id} introuvable : rien n'est enregistré`); return null; }
    const docType = fg.get('doc_type')?.value;
    const state = fg.get('state')?.value;
    if (docType !== DocType.LOCATION || Number(fg.get('data')?.value?.parent_order_id)) { this.alertService.warn(`La pièce n° ${id} n'est pas un dossier de location`); return null; }
    if (!isOrderLineEditable(state, docType)) {
      this.alertService.warn(`Dossier n° ${id} passé à « ${getOrderLabel(state)} » depuis l'ouverture de la page : rien n'est enregistré`);
      if (id === this.id) this.load(undefined, true);
      return null;
    }
    return fg;
  }

  // Écritures de commandes de vente du dossier : relues avant chaque écriture (#8), sous le spinner bloquant.
  private get saleOrderApi(): SaleOrderApi {
    const label = 'Commande de vente du dossier…';
    return {
      list: (id) => this.quotationService.getRentalSaleOrders(id).pipe(this.spin.block(label)),
      read: (id) => this.quotationService.getQuotationAsFormGroup(id).pipe(this.spin.block(label)),
      post: (typ, fg) => this.quotationService.postQuotationEvent(typ, fg).pipe(this.spin.block(label)),
    };
  }

  // « Vendre un article » (#54) : la F2 en création du dossier s'ouvre si elle existe, sinon une F2 vide naît en création ; même garde de période que toute écriture.
  async newSaleOrder(): Promise<void> {
    if (!this.id || !this.formGroup) return;
    const dossierId = this.id;
    if (!(await this.guardSave())) return;
    try {
      let saleId = reusableSaleOrder(await firstValueFrom(this.saleOrderApi.list(dossierId)), dossierId)?.id ?? 0;
      if (!saleId) {
        const fg = await this.readDossier();
        if (!fg || dossierId !== this.id) return;
        saleId = await createSaleOrder((sfg) => this.saleOrderApi.post(EVENTTYP_INSERT, sfg), saleOrderHeadOf(fg.getRawValue(), dossierId), this.loginService.loggeduser?.id ?? null, this.loginService.currentstore ?? null, []);
        this.alertService.success(`Commande de vente n° ${saleId} créée pour le dossier ${dossierId}`);
      }
      this.loadSaleOrders();
      if (!window.open(this.router.serializeUrl(this.router.createUrlTree(['/sales/quotation', saleId])), '_blank')) this.alertService.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site');
    } catch (e) {
      showError(`La commande de vente n'a pas été créée.\nDétails: ${e instanceof Error ? e.message : serverMessage(e)}`, 'ERREUR Serveur');
    } finally {
      this.saving = false;
    }
  }

  // Commandes de vente du dossier : lues à chaque relecture, jamais retirées de l'écran pendant la lecture suivante ; les avoirs n'en sont pas (#26).
  loadSaleOrders(): void {
    const id = this.id;
    if (!id) return;
    this.quotationService.getRentalSaleOrders(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (orders) => { if (id !== this.id) return; this.saleOrders = (orders ?? []).filter((o) => o.doc_type === DocType.FACTURE).sort((a, b) => b.id - a.id); this.saleOrdersError = null; },
      error: (e) => { if (id === this.id) this.saleOrdersError = 'Commandes de vente illisibles : ' + serverMessage(e); },
    });
  }

  saleOrderState(o: Sales_order): string { return getOrderLabel(o.state); }
  saleOrderCanceled(o: Sales_order): boolean { return o.state === SalesOrderState.CANCELED || o.state === SalesOrderState.REFUSED; }
  saleOrderAmount(o: Sales_order): string { return money(Number(o.amount_taxi) || 0) + ' € TTC'; }
  saleOrderLines(o: Sales_order): string { return (o.lines ?? []).map((l) => `${Number(l.qty)} × ${l.product_name ?? 'article'}`).join(' · '); }
  saleOrderBls(o: Sales_order): string {
    const bls = (o.lnk_deliveries ?? []).filter((d) => Number(d.mode) >= 0);
    const signed = bls.filter((d) => !!d.realdatdel).length;
    return !bls.length ? 'aucun BL' : `${signed} BL signé${signed > 1 ? 's' : ''} · ${bls.length - signed} à livrer`;
  }

  // Produits vendus mis de côté (produits liés du contrat) : ajoutés à la F2 en création du dossier, sinon à une F2 neuve (#27).
  // Un échec garde les lignes et propose « Réessayer » (#28) ; `keep` faux (brouillon, la page change) : abandonnées après avertissement.
  private async createPendingSales(head: SaleOrderHead, keep = true): Promise<number[]> {
    const lines = this.pendingSales;
    this.pendingSales = [];
    if (!lines.length) return [];
    for (;;) {
      try {
        const id = await placeSaleLines(this.saleOrderApi, head, this.loginService.loggeduser?.id ?? null, this.loginService.currentstore ?? null, lines);
        if (this.salesToRetry?.lines === lines) this.salesToRetry = null;
        this.alertService.success(`Commande de vente n° ${id} du dossier ${head.id} : ${this.plural(lines.length, 'produit vendu', 'produits vendus')}`);
        if (head.id === this.id) this.loadSaleOrders();
        return [id];
      } catch (e) {
        const why = e instanceof Error ? e.message : serverMessage(e);
        const r = await Swal.fire({
          titleText: 'Commande de vente non enregistrée', icon: 'error', showCancelButton: true, confirmButtonText: 'Réessayer', cancelButtonText: keep ? 'Plus tard' : 'Abandonner ces produits',
          text: `Le matériel est enregistré au dossier n° ${head.id}, mais ${this.plural(lines.length, 'produit vendu', 'produits vendus')} (${lines.map((l) => l.get('product_name')?.value).join(', ')}) n'${lines.length > 1 ? 'ont' : 'a'} pas pu entrer dans sa commande de vente : ${why}`,
        });
        if (r.isConfirmed) continue;
        if (keep && head.id === this.id) this.salesToRetry = { head, lines };
        else this.alertService.warn("Produits vendus abandonnés : ajoutez-les par « Vendre un article »");
        return [];
      }
    }
  }

  // « Réessayer » de la carte des ventes (#28) : les produits gardés partent dans la F2 du dossier, sans repasser par le matériel.
  async retrySales(): Promise<void> {
    const pending = this.salesToRetry;
    if (!pending || !(await this.guardSave())) return;
    try {
      this.pendingSales = pending.lines;
      await this.createPendingSales(pending.head);
    } finally {
      this.saving = false;
    }
  }

  // Le contrôle visé est relu par sa place ET son id : une relecture entre-temps ne fait pas écrire sur une autre ligne.
  private lineAt(index: number, id: number): FormGroup | null {
    const line = this.lines?.at(index) as FormGroup | undefined;
    return line && (Number(line.get('id')?.value) || 0) === id ? line : null;
  }

  // --- Patient (mémo de la ligne) ---

  editMemo(r: RentalRow): void {
    if (!this.editable || this.saving) return;
    this.editingIndex = r.index;
    this.editingMemo = r.patient;
  }

  cancelMemo(): void {
    this.editingIndex = null;
  }

  async saveMemo(r: RentalRow): Promise<void> {
    const value = this.editingMemo.trim();
    const line = this.lineAt(r.index, r.id);
    if (!line) { this.editingIndex = null; return; }
    if ((line.get('memo')?.value ?? '').trim() === value) { this.editingIndex = null; return; }
    const seen = line.getRawValue();
    if (!(await this.guardSave())) return;
    this.editingIndex = null;
    await this.commit((dossier) => {
      const l = freshLine(dossier, seen);
      if (typeof l === 'string') return l;
      l.get('memo')?.setValue(value);
      l.markAsDirty();
      return null;
    });
  }

  // --- « Les dates sont bonnes » : l'écart reste en base, l'avertissement est levé pour ces deux jours-là ---

  async confirmDates(r: RentalRow): Promise<void> {
    if (!this.editable || !r.anomalies.some((a) => a.dates)) return;
    const line = this.lineAt(r.index, r.id);
    if (!line) return;
    const seen = line.getRawValue();
    const answer = await Swal.fire({
      title: 'Dates correctes', icon: 'question', showCancelButton: true, confirmButtonText: 'Les dates sont bonnes', cancelButtonText: 'Annuler',
      text: `Livré le ${frDay(r.installedOn)}, repris le ${frDay(r.returnedOn)} : vous confirmez ces dates telles quelles ? L'avertissement disparaît ; il reviendra si l'une des deux dates change.`,
    });
    if (!answer.isConfirmed) return;
    if (!(await this.guardSave())) return;
    const user = this.loginService.loggeduser;
    const nom = `${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim() || user?.login || null;
    await this.commit((dossier) => {
      const l = freshLine(dossier, seen);
      if (typeof l === 'string') return l;
      l.get('data')?.setValue({ ...(l.get('data')?.value ?? {}), dates_verifiees: { livre: r.installedOn, repris: r.returnedOn, par: user?.id ?? null, nom, le: new Date().toISOString() } });
      l.markAsDirty();
      return null;
    });
  }

  checkedTitle(r: RentalRow): string {
    const c = r.datesChecked;
    if (!c) return '';
    const le = c.le ? ` le ${frDay(parisDay(c.le))}` : '';
    return `Dates déclarées correctes${c.nom ? ' par ' + c.nom : ''}${le}`;
  }

  // --- Récupérer : la ligne louée va à −1 sur un BL de reprise, aucune ligne n'est ajoutée (règle de JP du 29/10) ---

  collect(r: RentalRow): Promise<void> {
    return this.collectRows([r]);
  }

  // Plusieurs matériels (camion d'un patient) : cochés au choix, puis la fenêtre de livraison s'ouvre sur ce seul patient.
  // Sans produit lié « à la reprise », rien n'est enregistré : la fenêtre propose les lignes à −1 ; sinon les ventes liées sont ajoutées d'abord.
  private async collectRows(rows: RentalRow[], patient?: string): Promise<void> {
    rows = rows.filter((r) => r.canCollect);
    if (!this.editable || !rows.length) return;
    if (this.saving) { this.alertService.warn('Un enregistrement est déjà en cours'); return; }
    const fg = this.formGroup;
    const items = rows.flatMap((r) => {
      const install = this.lineAt(r.index, r.id);
      return install ? [{ r, install, raw: install.getRawValue() }] : [];
    });
    if (!items.length) return;

    let chosen = items;
    if (rows.length === 1) {
      const r = items[0].r;
      const who = r.patient ? `de ${r.patient}` : `de la ligne #${r.seq}`;
      const answer = await Swal.fire({ title: 'Récupérer', text: `Programmer la reprise du matériel ${who} ?`, icon: 'question', showCancelButton: true, confirmButtonText: 'Programmer la reprise', cancelButtonText: 'Annuler' });
      if (!answer.isConfirmed) return;
    } else {
      // Id de la ligne de commande, le même qu'au Planning : distingue deux lits identiques d'un même établissement.
      const lid = (id: number) => (id ? `<small style="margin-left:6px;color:#a3adba;font-size:10.5px" title="Identifiant de la ligne de commande">id ${id}</small>` : '');
      const list = items.map((x, i) => `<label style="display:block;text-align:left;margin:6px 0"><input type="checkbox" data-i="${i}" checked> ${esc(x.r.productName || 'Ligne #' + x.r.seq)}${lid(x.r.id)}</label>`).join('');
      const answer = await Swal.fire({
        title: 'Récupérer', icon: 'question', showCancelButton: true, confirmButtonText: 'Programmer la reprise', cancelButtonText: 'Annuler', width: '40em',
        html: `<p style="text-align:left">Matériel livré ou à livrer ${esc(patient ? 'à ' + patient : 'à ce patient')} : cochez ce qui reviendra.</p>${list}`,
        preConfirm: () => {
          const picked = [...(Swal.getHtmlContainer()?.querySelectorAll<HTMLInputElement>('input[data-i]') ?? [])].filter((c) => c.checked).map((c) => Number(c.dataset['i']));
          if (!picked.length) Swal.showValidationMessage('Cochez au moins un matériel');
          return picked.length ? picked : false;
        },
      });
      if (!answer.isConfirmed) return;
      chosen = (answer.value as number[]).map((i) => items[i]);
    }
    const collect = chosen.map((x) => x.r.id);
    const linked = chosen.flatMap((x) => linkedProductsFor(x.raw.rental_terms_snapshot, BillingTriggerEvent.RETURN).map((lp) => ({ lp, memo: x.raw.memo ?? '' })));
    if (!linked.length) { this.openDeliveryDialog([], patient, collect); return; }

    // Ventes liées à la reprise : mêmes lectures et mêmes refus que la carte « Livrer un matériel » (index jpid, tarif du client).
    if (!(await this.guardSave())) return;
    if (fg !== this.formGroup || chosen.some((x) => this.lineAt(x.r.index, x.r.id) !== x.install)) { this.saving = false; return; }
    const pricelists = this.customer?.data?.pricelists ?? this.customer?.lnk_partnerprices?.map((spp: { id: any }) => spp.id);
    let read: [any[], { id: number; name: string }[]];
    try {
      read = await firstValueFrom(forkJoin([
        forkJoin(linked.map(({ lp }) => this.productsService.search_productpackaging('jpid', lp.product_id, pricelists).pipe(catchError(() => of(null))))),
        this.valuesService.getValues$(C_VALUES_UOM).pipe(take(1), catchError(() => of([]))),
      ]).pipe(this.spin.block('Lecture des produits liés à la reprise…')));
    } catch (e) {
      this.saving = false;
      this.alertService.error('ERREUR Serveur : ' + serverMessage(e));
      return;
    }
    const sales: LinkedSale[] = linked.map(({ lp }, i) => linkedSaleOf(lp, read[0][i]));
    const bad = sales.find((x) => x.problem);
    if (bad) { this.saving = false; this.alertService.error(`Produit lié à la reprise ${bad.name} : ${bad.problem}`); return; }
    const notSold = sales.filter((x) => x.notSold);
    if (notSold.length) {
      const answer = await Swal.fire({ title: 'Produit lié non vendu', icon: 'warning', showCancelButton: true, confirmButtonText: 'Le vendre quand même', cancelButtonText: 'Annuler',
        text: `${notSold.map((x) => x.name).join(', ')} : article marqué non vendu. Le vendre quand même avec la reprise ?` });
      if (!answer.isConfirmed) { this.saving = false; return; }
    }
    // Produits liés à la reprise : une commande de vente du dossier, livrée dans le même passage que la reprise (rien n'entre au dossier).
    const dossier = await this.readDossier();   // en-tête de la F2 relu (#8)
    if (!dossier || fg !== this.formGroup) { this.saving = false; return; }
    this.pendingSales = sales.map((x, i) => newSaleLine(this.fb, x, i + 1, packagingNameOf(x.ppv, read[1] ?? []), linked[i].memo));
    const saleIds = await this.createPendingSales(saleOrderHeadOf(dossier.getRawValue(), this.id));
    this.saving = false;
    this.openDeliveryDialog([], patient, collect, saleIds);
  }

  // --- Livrer un matériel : carte partagée avec la vue du payeur (rental-add-line) ---

  // `memo` : + d'un patient du Planning ; la carte, au-dessus de la frise, est ramenée à l'écran.
  openAddForm(memo?: string): void {
    if (!this.editable) return;
    this.adding = true;
    setTimeout(() => this.addLine?.start(memo));
  }

  closeAddForm(): void {
    this.adding = false;
  }

  // Patients déjà présents proposés sous le champ Patient : en choisir un recopie son mémo à l'identique, donc même rangée et même BL.
  private patientsOf: { view: RentalView; list: PresentPatient[] } | null = null;
  get present(): PresentPatient[] {
    if (this.patientsOf?.view !== this.view) this.patientsOf = { view: this.view, list: presentPatients(this.view) };
    return this.patientsOf.list;
  }

  async saveAddForm(p: AddFormState): Promise<void> {
    if (!p?.product || !p.terms || p.vat == null || addFormProblem(p)) return;
    const fg = this.formGroup;
    if (!(await this.guardSave())) return;
    if (fg !== this.formGroup || !this.adding || this.addLine?.form !== p) { this.saving = false; return; }
    const ppv = await firstValueFrom(this.productsService.default_ppv_id(p.product.id).pipe(catchError(() => of(undefined)), this.spin.block('Préparation de la ligne…')));
    const isHt = this.isHt;   // unité du prix saisi
    this.adding = false;
    await this.commit((dossier) => {
      if (!this.draft && !!dossier.get('lnk_customer')?.value?.data?.cufs?.[cufSystem.PARTNER_IS_HT] !== isHt) return `Le client est passé ${isHt ? 'en TTC' : 'en HT'} depuis l'ouverture de la page : le prix saisi ne vaut plus. La page est rechargée.`;
      this.pendingSales = appendAddedLines(this.fb, dossier.get('lines') as FormArray, p, isHt, ppv ?? null);
      return null;
    }, true);
  }

  // Reprise hors contrat : une ligne RENTAL_RETURN liée à rien, puis la fenêtre de livraison s'ouvre sur elle (brouillon : le dossier naît avec elle).
  async saveReturnForm(p: ReturnFormState): Promise<void> {
    if (!p?.product || returnFormProblem(p)) return;
    const fg = this.formGroup;
    if (!(await this.guardSave())) return;
    if (fg !== this.formGroup || !this.adding || this.addLine?.ret !== p) { this.saving = false; return; }
    const ppv = await firstValueFrom(this.productsService.default_ppv_id(p.product.id).pipe(catchError(() => of(undefined)), this.spin.block('Préparation de la reprise…')));
    this.adding = false;
    await this.commit((dossier) => {
      const lines = dossier.get('lines') as FormArray;
      lines.push(newOutOfContractReturnLine(this.fb, p, ppv ?? null, highestSeq(lines) + 1));
      return null;
    }, true);
  }

  // --- Livraisons : fenêtre de livraison location (un BL par passage, lignes visées pré-cochées) ---

  // Camion d'un patient du Planning (JP 29/10) : ce qui reste à poser sur un BL s'ouvre dans la fenêtre réduite à ce patient ; le livré, ou à livrer (JP 04/11), se propose à la récupération.
  async planDeliveryFor(ids: number[]): Promise<void> {
    const rows = this.view.rows.filter((r) => ids.includes(r.id));
    const patient = rows[0]?.patient ?? '';
    const toPlan = rows.flatMap((r) => PLAN_STATES.has(r.state) ? [r.id] : []).filter((id) => id > 0);
    const toCollect = rows.filter((r) => r.canCollect);
    let collect = !toPlan.length;
    if (toPlan.length && toCollect.length) {
      const a = await Swal.fire({
        titleText: this.patientText(patient), icon: 'question', showDenyButton: true, showCancelButton: true,   // titleText : le mémo saisi n'est jamais lu comme du HTML (#9)
        text: `${this.plural(toPlan.length, 'matériel', 'matériels')} à livrer ou à reprendre, ${this.plural(toCollect.length, 'matériel à récupérer', 'matériels à récupérer')}.`,
        confirmButtonText: 'Planifier la livraison', denyButtonText: 'Programmer la reprise', cancelButtonText: 'Annuler',
      });
      if (a.isDismissed) return;
      collect = a.isDenied;
    }
    if (collect) await this.collectRows(toCollect, patient);
    else this.openDeliveryDialog(toPlan, patient);
  }

  // `collectIds` : lignes louées posées ou à poser, à reprendre, proposées à −1 sur le BL (règle de JP du 29/10 : la reprise n'est plus une ligne).
  // `saleOrderIds` : commandes de vente du dossier à livrer dans ce passage, sur leur propre BL (JP 30/10).
  openDeliveryDialog(lineIds: number[] = [], patient?: string, collectIds: number[] = [], saleOrderIds: number[] = []): void {
    if (!this.id || this.saving) return;
    const blocked = this.deliveryBlockedReason;
    if (blocked) { this.alertService.warn(blocked); return; }
    this.dialog.open(RentalDeliveryDialogComponent, { width: '90%', height: '90%', maxWidth: '95vw', data: { id: this.id, lineIds, ...(collectIds.length ? { collectIds } : {}), ...(saleOrderIds.length ? { saleOrderIds } : {}), ...(patient !== undefined ? { patient } : {}) } })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(undefined, true));
  }

  // « prévue le » : BL planifié mais pas signé, ouvert seul dans la fenêtre de livraison location (lecture sans l'historique du dossier).
  openBl(deliveryId: number): void {
    if (!this.id || !deliveryId || this.saving) return;
    this.dialog.open(RentalDeliveryDialogComponent, { width: '90%', height: '90%', maxWidth: '95vw', data: { id: this.id, deliveryId } })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load(undefined, true));
  }

  blTip(deliveryId: number): string { return `Voir le BL ${deliveryId}, planifié et pas encore signé`; }
}

// Brouillon de /rental/nouveau : la sortie pose la question de l'abandon (canLeave) ; un dossier existant ou enregistré s'ouvre sans elle.
export const rentalDraftCanDeactivate: CanDeactivateFn<RentalContractComponent> = (component) => component.canLeave();
